import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from '@ksyos/date-time';
import { kebabCase } from 'lodash';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import type { saveTypeEnum } from '@common/enums';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { isDateInBetweenTwoDates } from '@common/utils/dates';
import { CostInfoAndConsentModal } from '@frontend/components/elements/modals/costInfoAndConsentModal/CostInfoAndConsentModal';
import { getCostInfoAndConsent } from '@frontend/components/elements/modals/costInfoAndConsentModal/utilsCostInfoAndConsent';
import { translateKey } from '@frontend/translate';
import { isAutoSaveActiveByService, scrollValidationErrorIntoCenterViewInScrollContainer } from '@frontend/utils';
import { asyncDebounce } from '@frontend/utils/asyncDebounce';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import { prepareDiagnosticReportForSave } from '@frontend/utils/prepareDiagnosisForSave';
import type { IEpisodesAndJournals } from '../../../../common/validation/episodesAndJournal';
import type { AddOrEditType } from '../../../../common/validation/getProcedureValidationType';
import type { IReduxState } from '../../../configureStore';
import type { ValidationErrors } from '../../../utils/validation';
import type { IAllergy } from '../../interfaces/allergy';
import type { IAsset } from '../../interfaces/asset';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IInsurer } from '../../interfaces/insurer';
import type { LabResult } from '../../interfaces/labresult';
import type { IMedication } from '../../interfaces/medication';
import type { IOrganizationGetOne } from '../../interfaces/organizationGetOne';
import type { IProcedureGetOne } from '../../interfaces/procedureGetOne';
import type { IProcess } from '../../interfaces/process';
import type { IRequest } from '../../interfaces/request';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IServiceGetOne } from '../../interfaces/serviceGetOne';
import type { IDiagnosticReportAddEdit } from '../diagnosticReports/DiagnosticReportItemAddEdit';
import type { IItemState } from '../episodes/Episode';
import type { IEpisodeContext } from '../episodes/EpisodeContext';
import type { ReloadEpisode } from '../episodes/reloadEpisode';
import type { IPatientActiveInsuranceContext } from '../patients/PatientInsuranceHistoryContext';
import type { IServiceProvider } from './serviceProvider';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ProcedureStatus } from '../../../../common/enums/procedureStatus';
import { ReportCategories } from '../../../../common/enums/reportCategories';
import { serviceUsesBioGraders } from '../../../../common/utils/serviceUsesBioGraders';
import { getProcedureValidationType } from '../../../../common/validation/getProcedureValidationType';
import {
    getProcedureValidationSchema,
    getSaveAndSaveAndSendValidationSchema,
} from '../../../../common/validation/procedure';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { isServiceContractedByInsurer } from '../../../utils/isServiceContractedByInsurer';
import { patientUsesMedicationValidation } from '../../../utils/patientUsesMedicationValidation';
import { removeAllErrorsStartingWithKey } from '../../../utils/removeAllErrorsStartingWithKey';
import { renameErrorKeys } from '../../../utils/renameErrorKeys';
import { emptyValidationErrors, validate, validationErrorsToString } from '../../../utils/validation';
import { Component } from '../../elements/Component';
import { InsurerCeilingModal } from '../../elements/InsurerCeilingModal';
import { initializeCustomControl } from '../customControls/initializeCustomControl';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { getCareProviderIsActive } from '../customControls/utils/v1/getCareProviderIsActive';
import { validateCustomControlData } from '../customControls/validateCustomControlData';
import { validateDiagnosis } from '../customControls/validateDiagnosis';
import { validateJournals } from '../customControls/validateJournals';
import { validateMedications } from '../customControls/validateMedications';
import { validateOneCustomControlData } from '../customControls/validateOneCustomControlData';
import {
    initializeDiagnosticReport,
    initializeFromDiagnosticReport,
} from '../diagnosticReports/DiagnosticReportItemAddEdit';
import { PatientActiveInsuranceContext } from '../patients/PatientInsuranceHistoryContext';

interface IProcedureItemAddEditProps {
    diagnosticReport?: IDiagnosticReportGetOne;
    disableFormAutosave: boolean;
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    isNewDirectProcedure?: boolean;
    itemState?: IItemState;
    onCancel: (requestId: string | undefined) => Promise<void>;
    onReloadPatient?: () => Promise<void>;
    onSave: (requestId: string) => Promise<void>;
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void>;
    organizationIsPerformerProcedureAndReport?: boolean;
    procedure?: IProcedureGetOne;
    process?: IProcess;
    reloadProcess: (mode: ReloadEpisode) => Promise<void>;
    removeProcedure?: () => Promise<void>;
    request?: IRequestGetOne;
    scrollToNewProcess?: () => void;
    serviceId: string;
}

interface IProcedureItemAddEditReduxProps {
    employmentId: string;
    organizationId: string;
}

interface IProcedureResult {
    assets: [];
    customControlData: any;
    id: string;
    requestId: string;
}

export interface IProcedureAddEdit {
    allergies?: IAllergy[];
    assets: IAsset[];
    careProviderIsNotRequester?: boolean;
    customControlData: any;
    diagnosticReport: IDiagnosticReportAddEdit | null;
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    id?: string;
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void>;
    patientId: string;
    patientUsesMedication: boolean | null;
    procedureMedications?: IMedication[];
    remark: string | null;
    requestEpisodesAndJournal?: IEpisodesAndJournals;
    requesterOrganizationId: string | null;
    requesterPractitionerId: string | null;
    requestId?: string;
    requestLabResults?: LabResult[];
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
    status: ProcedureStatus;
}

interface IProcedureItemAddEditState {
    canAutoSave: boolean;
    errors: ValidationErrors;
    insurer?: IInsurer;
    insurerBlockedIsModalShown: boolean;
    isFirstTimeSaving: boolean;
    isLoading?: boolean;
    organization?: IOrganizationGetOne;
    price?: number;
    procedure?: IProcedureAddEdit;
    request: IRequest;
    requesterOrganization?: IOrganizationGetOne;
    saveType: 'draft' | 'final';
    service?: IServiceGetOne;
    serviceProvider?: IServiceProvider;
    showCostInfoAndConsentModal: boolean;
    vatRate?: number;
}

export function ProcedureItemAddEdit(props: IProcedureItemAddEditProps) {
    const episodeContext = useEpisodeContext();
    const patientInsuranceHistoryContext = useContext(PatientActiveInsuranceContext);
    const employmentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    return (
        <ProcedureItemAddEditWrapper
            {...props}
            {...episodeContext}
            {...patientInsuranceHistoryContext}
            employmentId={employmentId}
            organizationId={organizationId}
        />
    );
}
export class ProcedureItemAddEditWrapper extends Component<
    IProcedureItemAddEditProps & IProcedureItemAddEditReduxProps & IEpisodeContext & IPatientActiveInsuranceContext,
    IProcedureItemAddEditState
> {
    private abortableFetch = new AbortableFetch();
    private debouncedSave = asyncDebounce(this.saveProcedure.bind(this), 300);

    constructor(
        props: IProcedureItemAddEditProps &
            IProcedureItemAddEditReduxProps &
            IEpisodeContext &
            IPatientActiveInsuranceContext
    ) {
        super(props);

        this.state = {
            errors: emptyValidationErrors,
            saveType: 'draft',
            request: {
                id: this.props.request && this.props.request.id,
                isDirectProcedureAdded: this.isDirectProcedure() || false,
                serviceId: this.props.serviceId,
                patientId: this.props.patient.id,
                customControlData: this.props.request ? this.props.request.customControlData : null,
                patientUsesMedication: false,
                hasCostConsentPatient: this.props.request ? this.props.request.hasCostConsentPatient : null,
            },
            insurerBlockedIsModalShown: false,
            showCostInfoAndConsentModal: false,
            isFirstTimeSaving: !this.props.procedure?.id,
            canAutoSave: false,
        };
    }

    public async componentDidMount() {
        await this.setState({ isLoading: true });
        await this.load();
        if (
            this.props.isNewDirectProcedure &&
            this.props.scrollToNewProcess &&
            document.getElementsByName('newProcess')
        ) {
            this.props.scrollToNewProcess();
        }

        const costAndConsentInfo = await getCostInfoAndConsent(this.props.serviceId, this.props.request?.id);

        await this.setState({
            ...costAndConsentInfo,
            isLoading: false,
            canAutoSave: true,
        });

        if (this.state.service?.episodeCategory) {
            startView(`/episode/${this.state.service.episodeCategory.name}/${this.getDatadogViewName()}`);
        }
    }

    public async componentDidUpdate(
        prevProps: Readonly<IProcedureItemAddEditProps & IProcedureItemAddEditReduxProps & IEpisodeContext>,
        prevState: Readonly<IProcedureItemAddEditState>
    ) {
        if (prevProps.serviceId !== this.props.serviceId) {
            await this.setState(({ request }) => ({
                service: undefined,
                procedure: undefined,
                request: { ...request, serviceId: this.props.serviceId },
            }));
            await this.load();

            const costAndConsentInfo = await getCostInfoAndConsent(this.props.serviceId, this.props.request?.id);

            await this.setState({
                ...costAndConsentInfo,
                isLoading: false,
            });
        }

        if (
            prevState.isFirstTimeSaving !== this.state.isFirstTimeSaving ||
            prevState.procedure?.id !== this.state.procedure?.id
        ) {
            await this.setState({ canAutoSave: this.state.isFirstTimeSaving || !!this.state.procedure?.id });
        }
    }

    public componentWillUnmount() {
        this.props.setAutoSaveStatus('idle');
    }

    public render() {
        if (!this.state.service || !this.state.procedure || this.state.isLoading) {
            return null;
        }

        if (this.state.insurer && this.state.insurerBlockedIsModalShown) {
            return (
                <InsurerCeilingModal
                    insurer={this.state.insurer}
                    isOpen={this.state.insurerBlockedIsModalShown}
                    onClose={async () => {
                        await this.setState({ insurerBlockedIsModalShown: false });
                        this.props.onCancel(this.state.request ? this.state.request.id : undefined);
                    }}
                ></InsurerCeilingModal>
            );
        }

        if (this.state.showCostInfoAndConsentModal) {
            return (
                <CostInfoAndConsentModal
                    isOpen={this.state.showCostInfoAndConsentModal}
                    onCancel={() => {
                        this.handleCostInfoAndConsentModal(false);
                    }}
                    onSubmit={(consent) => {
                        this.handleCostInfoAndConsentModal(consent);
                    }}
                    patientId={this.state.request.patientId}
                    price={this.state.price!}
                    serviceName={
                        getServiceLabel(this.state.service.code, 'costInfoAndConsentModal') ||
                        translateKey('common.service')
                    }
                    vatRate={this.state.vatRate!}
                />
            );
        }

        // CS-3667 for version not nullable
        const version = this.props.request
            ? this.props.request.customControlVersion
            : this.state.service.customControlVersion;
        if (!version) {
            throw new Error('Version or employmentId or organizationId not set');
        }

        if (this.isBioGrader()) {
            const CustomControl = loadCustomControl(
                version,
                CustomControlEntity.ProcedureDiagnosticReportAddEdit,
                this.state.service.code,
                this.state.service.serviceType
            );

            return (
                <CustomControl
                    currentDiagnosticReport={this.props.diagnosticReport}
                    currentProcedure={this.props.procedure}
                    diagnosticReport={this.state.procedure.diagnosticReport!}
                    employmentId={this.props.employmentId}
                    errors={this.state.errors}
                    formId={this.props.formId}
                    handleChange={async (name, value) => await this.handleChange(name, value)}
                    handleChangeCustomControl={async (values) => await this.handleChangeCustomControlProcedure(values)}
                    handleChangeCustomControlDiagnosticReport={async (values) =>
                        await this.handleChangeCustomControlDiagnosticReport(values)
                    }
                    handleChangeDiagnosticReport={async (name, value) =>
                        await this.handleChangeDiagnosticReport(name, value)
                    }
                    isAutoSaveActive={isAutoSaveActiveByService(
                        this.state.service.code,
                        this.props.disableFormAutosave
                    )}
                    isFirstItem={this.props.isFirstItem}
                    isHighPrivacy={Boolean(this.state.organization?.isHighPrivacy)}
                    itemState={this.props.itemState}
                    onCancel={() => this.props.onCancel(this.state.request.id)}
                    onSave={this.props.onSave}
                    onSaveProcedure={async (saveType) => {
                        await this.setState({ canAutoSave: false });

                        return await this.debouncedSave(saveType);
                    }}
                    organizationId={this.props.organizationId}
                    procedure={this.state.procedure}
                    process={this.props.process}
                    reloadProcess={this.props.reloadProcess}
                    removeProcedure={this.props.removeProcedure}
                    request={this.state.request}
                    requesterIsHighPrivacy={Boolean(this.state.requesterOrganization?.isHighPrivacy)}
                    revertProcedureChanges={this.revertProcedureChanges.bind(this)}
                    service={this.state.service}
                    serviceProvider={this.state.serviceProvider}
                    validateOne={async (name) => await this.validateOne(name)}
                    validateOneCustomControl={async (name, index) =>
                        await this.validateOneCustomControlProcedure(name, index)
                    }
                    validateOneCustomControlDiagnosticReport={async (name, customErrorRoute) =>
                        await this.validateOneCustomControlDiagnosticReport(name, customErrorRoute)
                    }
                    validateOneDiagnosticReport={async (name) => await this.validateOneDiagnosticReport(name)}
                    validateProcedure={async (saveType) => await this.validateProcedure(saveType)}
                />
            );
        } else {
            const CustomControl = loadCustomControl(
                version,
                CustomControlEntity.ProcedureAddEdit,
                this.state.service.code,
                this.state.service.serviceType
            );

            return (
                <CustomControl
                    currentProcedure={this.props.procedure}
                    employmentId={this.props.employmentId}
                    errors={this.state.errors}
                    handleChange={async (name, value) => await this.handleChange(name, value)}
                    handleChangeCustomControl={async (values) => await this.handleChangeCustomControlProcedure(values)}
                    isAutoSaveActive={isAutoSaveActiveByService(
                        this.state.service.code,
                        this.props.disableFormAutosave
                    )}
                    isFirstItem={this.props.isFirstItem}
                    isHighPrivacy={Boolean(this.state.organization?.isHighPrivacy)}
                    itemState={this.props.itemState}
                    onCancel={() => this.props.onCancel(this.state.request.id)}
                    onSave={this.props.onSave}
                    onSaveProcedure={async (saveType) => {
                        await this.setState({ canAutoSave: false });

                        return await this.debouncedSave(saveType);
                    }}
                    organizationId={this.props.organizationId}
                    procedure={this.state.procedure}
                    process={this.props.process}
                    removeProcedure={this.props.removeProcedure}
                    request={this.state.request}
                    requesterIsHighPrivacy={Boolean(this.state.requesterOrganization?.isHighPrivacy)}
                    revertProcedureChanges={this.revertProcedureChanges.bind(this)}
                    service={this.state.service}
                    serviceProvider={this.state.serviceProvider}
                    validateOne={async (name) => await this.validateOne(name)}
                    validateOneCustomControl={async (name) => await this.validateOneCustomControlProcedure(name)}
                    validateProcedure={async (saveType) => await this.validateProcedure(saveType)}
                />
            );
        }
    }

    private async handleCostInfoAndConsentModal(costConsentPatient: boolean) {
        await this.setState(({ request }) => ({
            showCostInfoAndConsentModal: false,
            request: {
                ...request,
                hasCostConsentPatient: costConsentPatient,
            },
        }));

        await this.props.onReloadPatient?.();
    }

    private async handleChange<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ procedure }) => ({ procedure: { ...procedure, [name]: value } }));
        if (name === 'assets') {
            await this.autoSaveProcedure(this.state.errors);
        } else if (
            this.state.serviceProvider &&
            !this.state.serviceProvider.isServiceConsumerSelector &&
            name === 'draftCareProviderOrganizationId'
        ) {
            await this.setState(({ procedure }) => ({
                procedure: { ...procedure, requesterOrganizationId: value },
            }));
        } else if (
            this.state.serviceProvider &&
            !this.state.serviceProvider.isServiceConsumerSelector &&
            name === 'draftCareProviderPractitionerId'
        ) {
            await this.setState(({ procedure }) => ({
                procedure: { ...procedure, requesterPractitionerId: value },
            }));
        }
    }

    private async validateOne(name: string) {
        if (!this.state.service || !this.state.procedure) {
            return;
        }

        if (name === 'selectedDiagnosticReportProviderOrganizationId') {
            await this.setState((state) =>
                state.procedure
                    ? {
                          errors: this.addSelectedDiagnosticReportProviderValidation(
                              state.procedure.selectedDiagnosticReportProviderOrganizationId,
                              state.errors
                          ),
                      }
                    : undefined
            );
        }

        if (name === 'draftCareProviderOrganizationId') {
            await this.setState((state) =>
                state.procedure
                    ? {
                          errors: this.addCareProviderValidation(
                              state.procedure.draftCareProviderOrganizationId,
                              state.errors
                          ),
                      }
                    : undefined
            );
        }

        if (name === 'procedureMedications') {
            await this.setState((state) =>
                state.procedure
                    ? { errors: validateMedications(state.errors, state.procedure.procedureMedications) }
                    : undefined
            );
        }

        if (name === 'requestEpisodesAndJournal') {
            await this.setState((state) =>
                state.procedure
                    ? {
                          errors: validateJournals(
                              state.errors,
                              state.procedure.requestEpisodesAndJournal?.journalEntries
                          ),
                      }
                    : undefined
            );
        }

        let errors = emptyValidationErrors;
        const validationErrors = this.validateAll('final');

        if (validationErrors.has(name)) {
            errors = errors.set(name, validationErrors.get(name));
        } else {
            errors = errors.remove(name);
        }

        if (!validationErrors.isEmpty()) {
            this.props.setAutoSaveStatus('idle');
        }

        await this.autoSaveProcedure(errors);

        await this.setState({ errors });
    }

    private async handleChangeCustomControlProcedure<T extends object>(values: T) {
        await this.setState(({ procedure }) => ({
            procedure: { ...procedure, customControlData: { ...procedure!.customControlData, ...values } },
        }));

        if (('files' in values || 'assets' in values) && Object.keys(values).length === 1) {
            await this.autoSaveProcedure(this.state.errors);
        }
    }

    private async validateOneCustomControlProcedure(name: string, index?: number) {
        let customControlName = 'procedure.customControlData.' + name;
        if (index !== undefined) {
            customControlName += '.' + index.toString();
        }
        await this.validateCustomControl(customControlName, name, CustomControlEntity.ProcedureAddEdit);

        await this.autoSaveProcedure(this.state.errors);
    }

    private async handleChangeDiagnosticReport<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ procedure }) => ({
            procedure: {
                ...procedure,
                diagnosticReport: {
                    ...procedure!.diagnosticReport,
                    [name]: value,
                },
            },
        }));
    }

    private async handleChangeCustomControlDiagnosticReport(values: any) {
        await this.setState(({ procedure }) => ({
            procedure: {
                ...procedure,
                diagnosticReport: {
                    ...procedure!.diagnosticReport,
                    customControlData: { ...procedure!.diagnosticReport!.customControlData, ...values },
                },
            },
        }));
    }

    private async validateOneDiagnosticReport(name: string) {
        await this.validateCustomControl('diagnosticReport.' + name, name, CustomControlEntity.DiagnosticReportAddEdit);
        await this.autoSaveProcedure(this.state.errors);
    }

    private async validateOneCustomControlDiagnosticReport(name: string, customErrorRoute?: string) {
        await this.validateCustomControl(
            customErrorRoute ?? 'diagnosticReport.customControlData.' + name,
            name,
            CustomControlEntity.DiagnosticReportAddEdit
        );
        await this.autoSaveProcedure(this.state.errors);
    }

    private async validateCustomControl(
        customControlName: string,
        name: string,
        customControlEntity: CustomControlEntity
    ) {
        if (!this.state.service || !this.state.procedure) {
            return;
        }

        await this.setState(({ procedure, errors }) => {
            const validationErrors = this.validateAll('final');

            const searchKey = customControlName.replace(/^procedure./, '');
            if (validationErrors.has(searchKey)) {
                errors = errors.set(searchKey, validationErrors.get(searchKey));
            } else {
                errors = errors.remove(searchKey);
            }
            const deeperError = validationErrors.keySeq().find((errorKey) => !!errorKey?.startsWith(`${searchKey}.`));

            if (deeperError) {
                errors = errors.set(deeperError, validationErrors.get(deeperError));
            } else {
                errors = removeAllErrorsStartingWithKey(`${searchKey}.`, errors);
            }

            if (this.isBioGrader()) {
                errors = validateOneCustomControlData(
                    this.getVersion()!,
                    CustomControlEntity.ProcedureDiagnosticReportAddEdit,
                    this.state.service!.code,
                    procedure!.customControlData,
                    name,
                    errors,
                    this.props.process || undefined,
                    procedure
                );
            }
            if (customControlEntity === CustomControlEntity.ProcedureAddEdit) {
                errors = validateOneCustomControlData(
                    this.getVersion()!,
                    customControlEntity,
                    this.state.service!.code,
                    procedure!.customControlData,
                    name,
                    errors,
                    this.props.process || undefined
                );
            } else if (customControlEntity === CustomControlEntity.DiagnosticReportAddEdit) {
                errors = validateOneCustomControlData(
                    this.getVersion()!,
                    customControlEntity,
                    this.state.service!.code,
                    procedure!.diagnosticReport!.customControlData,
                    name,
                    errors,
                    this.props.process || undefined,
                    undefined,
                    undefined,
                    procedure!.diagnosticReport!.diagnosis
                );
            }

            return { errors };
        });
    }

    private addSelectedDiagnosticReportProviderValidation(
        selectedDiagnosticReportProviderOrganizationId: string | null,
        errors: ValidationErrors
    ) {
        if (
            this.state.service &&
            !this.state.service.userIsGrader &&
            this.state.service.userIsProviderGraderSelector &&
            !selectedDiagnosticReportProviderOrganizationId &&
            (!this.props.process || !this.props.process.selectedDiagnosticReportProviderPractitionerId)
        ) {
            return errors.set('selectedDiagnosticReportProviderOrganizationId', 'graderRequired');
        }

        return errors;
    }

    private addCareProviderValidation(draftCareProviderOrganizationId: string | null, errors: ValidationErrors) {
        if (this.state.requesterOrganization?.isHighPrivacy || this.state.service?.noGeneralPractitionerInvolved) {
            return errors;
        }
        if (
            this.state.organization &&
            !draftCareProviderOrganizationId &&
            (!this.state.organization.isHighPrivacy ||
                (this.state.serviceProvider && !this.state.serviceProvider.isServiceConsumerSelector)) &&
            !this.state.procedure?.careProviderIsNotRequester
        ) {
            return errors.set('draftCareProviderOrganizationId', 'any_required');
        }

        return errors;
    }

    private async load() {
        await this.loadService();

        if (!this.state.service) {
            throw new Error('unable to find service');
        }

        await this.loadServiceProvider();

        const careProviderIsActive = await getCareProviderIsActive(
            this.props.patient.careProviderPractitionerId,
            this.props.patient.careProviderOrganizationId
        );
        const careProviderPractitionerId =
            careProviderIsActive && this.props.patient.careProviderPractitionerId
                ? this.props.patient.careProviderPractitionerId
                : null;
        const careProviderOrganizationId =
            careProviderIsActive && this.props.patient.careProviderOrganizationId
                ? this.props.patient.careProviderOrganizationId
                : null;

        let diagnosticReport = null;
        if (this.props.diagnosticReport) {
            diagnosticReport = initializeFromDiagnosticReport(this.props.diagnosticReport);
            if (this.isBioGrader() && !diagnosticReport.customControlData) {
                diagnosticReport.customControlData = initializeCustomControl(
                    this.getVersion(),
                    CustomControlEntity.DiagnosticReportAddEdit,
                    this.getCode(),
                    this.getServiceType(),
                    undefined,
                    this.props.diagnosticReport.category
                );
            }
        } else if (this.isBioGrader()) {
            const diagnosticReportCustomControlData = initializeCustomControl(
                this.getVersion(),
                CustomControlEntity.DiagnosticReportAddEdit,
                this.getCode(),
                this.getServiceType(),
                this.isDirectProcedure(),
                ReportCategories.examinationReport
            );
            if (diagnosticReportCustomControlData !== null) {
                const requestId = this.props.request ? this.props.request.id : undefined;
                diagnosticReport = initializeDiagnosticReport(
                    this.props.patient.id,
                    ReportCategories.examinationReport,
                    requestId,
                    diagnosticReportCustomControlData
                );
            }
        }
        if (this.props.request) {
            if (this.props.procedure) {
                // set initial procedure
                // define requesterOrganization and Practitioner
                let requesterOrganizationId;
                let requesterPractitionerId;
                if (
                    !this.state.request.isDirectProcedureAdded ||
                    (this.state.serviceProvider && this.state.serviceProvider.isServiceConsumerSelector) ||
                    this.state.service.noGeneralPractitionerInvolved
                ) {
                    requesterOrganizationId = this.props.request.requesterOrganizationId;
                    requesterPractitionerId = this.props.request.requesterPractitionerId;
                } else if (
                    this.props.patient.careProviderOrganizationId &&
                    this.props.patient.careProviderPractitionerId &&
                    careProviderIsActive
                ) {
                    requesterOrganizationId = this.props.patient.careProviderOrganizationId;
                    requesterPractitionerId = this.props.patient.careProviderPractitionerId;
                } else {
                    requesterOrganizationId = this.props.request.draftCareProviderOrganizationId;
                    requesterPractitionerId = this.props.request.draftCareProviderPractitionerId;
                }

                await this.setState({
                    procedure: {
                        ...this.props.procedure,
                        assets: this.props.procedure.assets ? this.props.procedure.assets : [],
                        selectedDiagnosticReportProviderOrganizationId:
                            this.props.procedure.selectedDiagnosticReportProviderOrganizationId,
                        selectedDiagnosticReportProviderPractitionerId:
                            this.props.procedure.selectedDiagnosticReportProviderPractitionerId,
                        diagnosticReport,
                        customControlData: this.props.procedure.customControlData,
                        requesterOrganizationId,
                        requesterPractitionerId,
                        draftCareProviderOrganizationId:
                            this.props.request.draftCareProviderOrganizationId || careProviderOrganizationId,
                        draftCareProviderPractitionerId:
                            this.props.request.draftCareProviderPractitionerId || careProviderPractitionerId,
                    },
                });
            } else {
                const customControlData = initializeCustomControl(
                    this.props.request.customControlVersion,
                    CustomControlEntity.ProcedureAddEdit,
                    this.getCode(),
                    this.getServiceType()
                );

                await this.setState({
                    procedure: {
                        assets: [],
                        remark: null,
                        patientId: this.props.patient.id,
                        requestId: this.props.request.id,
                        status: ProcedureStatus.inProgress,
                        selectedDiagnosticReportProviderOrganizationId: null,
                        selectedDiagnosticReportProviderPractitionerId: null,
                        customControlData,
                        diagnosticReport,
                        requesterOrganizationId: this.props.request.requesterOrganizationId,
                        requesterPractitionerId: this.props.request.requesterPractitionerId,
                        draftCareProviderOrganizationId:
                            this.props.request.draftCareProviderOrganizationId || careProviderOrganizationId,
                        draftCareProviderPractitionerId:
                            this.props.request.draftCareProviderPractitionerId || careProviderPractitionerId,
                        patientUsesMedication: false,
                    },
                });
            }
        } else {
            const customControlData = initializeCustomControl(
                this.getVersion(),
                CustomControlEntity.ProcedureAddEdit,
                this.getCode(),
                this.getServiceType(),
                this.isDirectProcedure()
            );

            await this.setState({
                procedure: {
                    patientId: this.props.patient.id,
                    status: ProcedureStatus.inProgress,
                    remark: null,
                    selectedDiagnosticReportProviderOrganizationId: null,
                    selectedDiagnosticReportProviderPractitionerId: null,
                    assets: [],
                    customControlData,
                    diagnosticReport,
                    draftCareProviderOrganizationId: careProviderOrganizationId,
                    draftCareProviderPractitionerId: careProviderPractitionerId,
                    patientUsesMedication: null,
                    ...this.getRequesterForNewProcedure(careProviderOrganizationId, careProviderPractitionerId),
                },
            });
        }

        await this.loadOrganization();
        await this.loadRequesterOrganization();

        if (!this.props.request) {
            await this.checkInsurerCeiling();
        }
    }

    private getRequesterForNewProcedure(
        careProviderOrganizationId: string | null,
        careProviderPractitionerId: string | null
    ): { requesterOrganizationId: string | null; requesterPractitionerId: string | null } {
        if (
            this.state.serviceProvider?.isServiceConsumerSelector ||
            this.state.service?.noGeneralPractitionerInvolved
        ) {
            return { requesterOrganizationId: null, requesterPractitionerId: null };
        } else {
            return {
                requesterOrganizationId: careProviderOrganizationId,
                requesterPractitionerId: careProviderPractitionerId,
            };
        }
    }

    private async loadService() {
        const url = `/api/services/${this.props.serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private async loadOrganization() {
        const url = `/api/organizations/${this.props.organizationId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ organization: result.json });
    }

    private async loadRequesterOrganization() {
        if (!this.props.request?.requesterOrganizationId) {
            return;
        }
        const url = `/api/organizations/${this.props.request.requesterOrganizationId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ requesterOrganization: result.json });
    }

    private getProcedureValidationType(draftOrFinal: 'draft' | 'final') {
        let addOrEdit: AddOrEditType;
        if (this.isBioGrader()) {
            if (this.state.procedure && this.state.procedure.id) {
                if (this.isDirectProcedure()) {
                    addOrEdit = 'editProcedureDiagnosticReportWithRequest';
                } else {
                    addOrEdit = 'editProcedureDiagnosticReport';
                }
            } else {
                if (this.isDirectProcedure()) {
                    addOrEdit = 'addProcedureDiagnosticReportWithRequest';
                } else {
                    addOrEdit = 'addProcedureDiagnosticReport';
                }
            }
        } else {
            if (this.state.procedure && this.state.procedure.id) {
                if (this.isDirectProcedure()) {
                    addOrEdit = 'editProcedureWithRequest';
                } else {
                    addOrEdit = 'editProcedure';
                }
            } else {
                if (this.isDirectProcedure()) {
                    addOrEdit = 'addProcedureWithRequest';
                } else {
                    addOrEdit = 'addProcedure';
                }
            }
        }

        return getProcedureValidationType(this.getCode(), this.getVersion(), addOrEdit, draftOrFinal);
    }

    private async getCostInfoAndConsent() {
        const url = `/api/services/${this.props.serviceId}/costInfo`;
        const result = (await this.abortableFetch.getJson(url)).json;

        if (result.price) {
            await this.setState({ price: result.price });
        }

        return result.shouldShowCostInfo;
    }

    private async costConsentPatientValidation(hasCostConsentPatient: boolean | null) {
        if (!hasCostConsentPatient) {
            const shouldGetPatientConsent = await this.getCostInfoAndConsent();

            return !shouldGetPatientConsent;
        }

        return true;
    }

    private isBioGrader() {
        return (
            this.props.organizationIsPerformerProcedureAndReport ||
            (this.state.serviceProvider &&
                this.state.serviceProvider.isGrader &&
                this.state.service &&
                serviceUsesBioGraders(this.state.service.code)) ||
            false
        );
    }

    private getVersion() {
        return this.props.request ? this.props.request.customControlVersion : this.state.service!.customControlVersion!;
    }

    private getServiceType() {
        return this.props.request ? this.props.request.service.serviceType : this.state.service!.serviceType;
    }

    private getCode() {
        return this.props.request ? this.props.request.service.code : this.state.service!.code;
    }

    private async validateProcedure(saveType: 'draft' | 'final') {
        if (!this.state.procedure) {
            return false;
        }
        if (
            saveType === 'final' &&
            !this.state.service?.noGeneralPractitionerInvolved &&
            !this.state.requesterOrganization?.isHighPrivacy &&
            this.state.serviceProvider &&
            this.state.serviceProvider.canSelectCareProviderAsServiceConsumer &&
            !this.state.procedure.careProviderIsNotRequester
        ) {
            await this.setState(({ procedure }: IProcedureItemAddEditState) => ({
                procedure: {
                    ...procedure,
                    requesterOrganizationId: procedure && procedure.draftCareProviderOrganizationId,
                    requesterPractitionerId: procedure && procedure.draftCareProviderPractitionerId,
                },
            }));
        }

        let errors = this.validateAll(saveType);

        if (saveType === 'final') {
            if (this.isBioGrader()) {
                errors = validateCustomControlData(
                    this.getVersion(),
                    CustomControlEntity.ProcedureDiagnosticReportAddEdit,
                    this.getCode(),
                    this.state.procedure.customControlData,
                    errors,
                    this.props.process,
                    this.state.procedure
                );
                if (this.state.procedure.diagnosticReport) {
                    errors = validateCustomControlData(
                        this.getVersion(),
                        CustomControlEntity.DiagnosticReportAddEdit,
                        this.getCode(),
                        this.state.procedure.diagnosticReport.customControlData,
                        errors,
                        this.props.process,
                        undefined,
                        undefined,
                        this.state.procedure.diagnosticReport.diagnosis
                    );
                    errors = validateDiagnosis(
                        this.getVersion(),
                        CustomControlEntity.DiagnosticReportAddEdit,
                        this.getCode(),
                        this.state.procedure.diagnosticReport.diagnosis,
                        errors,
                        this.state.procedure.diagnosticReport.customControlData
                    );
                }
            }
            errors = validateCustomControlData(
                this.getVersion(),
                CustomControlEntity.ProcedureAddEdit,
                this.getCode(),
                this.state.procedure.customControlData,
                errors,
                this.props.process
            );
            errors = this.addSelectedDiagnosticReportProviderValidation(
                this.state.procedure.selectedDiagnosticReportProviderOrganizationId,
                errors
            );
            errors = this.addCareProviderValidation(this.state.procedure.draftCareProviderOrganizationId, errors);
            errors = this.addPatientUsesMedicationValidation(
                this.state.service,
                this.state.procedure.patientUsesMedication,
                errors
            );
        }

        errors = validateMedications(errors, this.state.procedure?.procedureMedications);
        errors = validateJournals(errors, this.state.procedure.requestEpisodesAndJournal?.journalEntries);

        await this.setState({ errors, saveType });
        if (errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, errors);

            return false;
        }

        return true;
    }

    private addPatientUsesMedicationValidation(
        service: IServiceGetOne | undefined,
        patientUsesMedication: boolean | null,
        errors: ValidationErrors
    ) {
        if (this.isDirectProcedure() && service) {
            return patientUsesMedicationValidation(
                'procedure',
                service.code,
                service.serviceType,
                patientUsesMedication,
                errors
            );
        }

        return errors;
    }

    private validateAll(saveType: 'draft' | 'final') {
        if (!this.state.procedure) {
            return emptyValidationErrors;
        }

        const validationType = this.getProcedureValidationType(saveType);
        const validatedProcedure = validate(getProcedureValidationSchema(validationType), {
            request: {
                ...this.state.request,
                ...this.state.procedure,
                id: this.state.request.id,
            },
            procedure: {
                ...this.state.request,
                ...this.state.procedure,
                id: this.state.procedure.id,
            },
            diagnosticReport: this.state.procedure.diagnosticReport || null,
        });

        const errors = renameErrorKeys(validatedProcedure.errors, 'request.', '');

        return renameErrorKeys(errors, 'procedure.', '');
    }

    private isDirectProcedure() {
        return (
            this.props.isNewDirectProcedure ||
            (this.props.request && this.props.request.isDirectProcedureAdded) ||
            false
        );
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        this.props.setAutoSaveStatus('saving');

        if (!this.state.procedure) {
            return;
        }

        await this.setState({ isFirstTimeSaving: false });

        if (saveType === 'final') {
            const validConsent = await this.costConsentPatientValidation(this.state.request.hasCostConsentPatient);
            if (!validConsent) {
                await this.setState({ showCostInfoAndConsentModal: true });

                return;
            }
        }

        const validationType = this.getProcedureValidationType(saveType);
        const validatedProcedure = validate(getSaveAndSaveAndSendValidationSchema(validationType), {
            assets: this.state.procedure.assets,
            request: {
                ...this.state.request,
                ...this.state.procedure,
                id: this.state.request.id,
            },
            procedure: { ...this.state.request, ...this.state.procedure, id: this.state.procedure.id },
            diagnosticReport: prepareDiagnosticReportForSave(
                this.state.procedure.diagnosticReport || null,
                saveType as saveTypeEnum
            ),

            medications: this.state.procedure.procedureMedications,
            labResults: this.state.procedure.requestLabResults,
            episodesAndJournals: this.state.procedure.requestEpisodesAndJournal,
            allergies: this.state.procedure.allergies,
            validationType,
        });

        if (validatedProcedure.errors.size > 0) {
            if (
                isAutoSaveActiveByService(this.state.service!.code, this.props.disableFormAutosave) &&
                saveType === 'draft'
            ) {
                return;
            } else {
                throw new Error(
                    `Errors in save procedure validation:\n${validationErrorsToString(validatedProcedure.errors)}`
                );
            }
        }

        const url = '/api/procedures/' + (saveType === 'draft' ? 'save' : 'saveAndSend');
        const result = await fetchJson(url, 'POST', validatedProcedure.item);

        if (saveType === 'final') {
            // we cannot distinguish between save as draft and autosave so we only log the save final
            datadogRum.addAction(`Save ${kebabCase(this.getDatadogViewName())} complete`);
        }

        const procedureResult: IProcedureResult = (
            await fetchJson(`/api/procedures/${result.json.id}?showAssets=true`, 'GET')
        ).json;

        let draftDiagnosticReportId: string | undefined;
        if (this.state.procedure?.diagnosticReport && !this.state.procedure?.diagnosticReport.id) {
            const diagnosticReportsResult: { category: ReportCategories; id: string }[] = (
                await fetchJson(`/api/diagnosticReports?requestId=${procedureResult.requestId}`, 'GET')
            ).json.items.filter((report: any) => report.category === this.state.procedure?.diagnosticReport?.category);

            draftDiagnosticReportId = diagnosticReportsResult.length > 0 ? diagnosticReportsResult[0].id : undefined;
        }

        let customControlData: any = null;
        if (this.state.procedure && this.state.procedure.customControlData) {
            customControlData = {
                ...this.state.procedure.customControlData,
                files: procedureResult.customControlData
                    ? procedureResult.customControlData.files
                    : this.state.procedure!.customControlData && this.state.procedure!.customControlData.files,
            };
        }

        await this.setState(({ request, procedure }: IProcedureItemAddEditState) => ({
            procedure: {
                ...procedure,
                id: procedureResult.id,
                requestId: procedureResult.requestId,
                assets: procedureResult.assets,
                customControlData,
                diagnosticReport: draftDiagnosticReportId
                    ? {
                          ...procedure?.diagnosticReport,
                          id: draftDiagnosticReportId,
                          requestId: procedureResult.requestId,
                      }
                    : procedure?.diagnosticReport,
            },
            request: {
                ...request,
                id: procedureResult.requestId,
            },
        }));

        if (this.props.onReloadPatient && saveType === 'final') {
            await this.props.onReloadPatient();
        }

        this.props.setAutoSaveStatus('saved');

        return procedureResult.requestId;
    }

    private async loadServiceProvider() {
        const url = `/api/serviceProviders?employmentId=${this.props.employmentId}&serviceId=${this.props.serviceId}&isActive=true`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ serviceProvider: result.json.items[0] });
    }

    private async checkInsurerCeiling() {
        const activeInsuranceHistory = this.props.activeInsurance;
        if (activeInsuranceHistory?.insurer) {
            const insurerIsBlocked = isDateInBetweenTwoDates(
                DateTime.now().format('YYYY-MM-DD'),
                activeInsuranceHistory.insurer.turnoverCeilingStartDate,
                activeInsuranceHistory.insurer.turnoverCeilingEndDate
            );
            if (insurerIsBlocked) {
                const isServiceContracted = await isServiceContractedByInsurer(
                    activeInsuranceHistory.insurer.id,
                    this.props.serviceId
                );
                if (isServiceContracted) {
                    await this.setState({ insurer: activeInsuranceHistory.insurer, insurerBlockedIsModalShown: true });
                }
            }
        }
    }

    private async autoSaveProcedure(errors: ValidationErrors) {
        if (
            errors.size === 0 &&
            isAutoSaveActiveByService(this.state.service!.code, this.props.disableFormAutosave) &&
            (await this.validateProcedure('draft')) &&
            this.state.canAutoSave
        ) {
            await this.debouncedSave('draft');
        }
    }

    private async revertProcedureChanges() {
        if (this.props.procedure) {
            const originalProcedure: Partial<IProcedureAddEdit> | undefined = {
                ...this.state.procedure,
                ...this.props.procedure,
                diagnosticReport: this.state.procedure?.diagnosticReport
                    ? {
                          ...this.state.procedure?.diagnosticReport,
                          ...(this.props.diagnosticReport as IDiagnosticReportAddEdit),
                      }
                    : null,
            };

            await this.setState({ procedure: originalProcedure as IProcedureAddEdit });
            await this.debouncedSave('draft');
        } else {
            if (this.props.request?.id && this.state.procedure?.id) {
                const url = `/api/procedures/${this.state.procedure.id}`;
                await fetchJson(url, 'DELETE');
            }

            await this.props.removeProcedure?.();
        }
    }

    private getDatadogViewName() {
        let viewName = 'Procedure';
        if (this.props.isNewDirectProcedure) {
            viewName = 'Request_' + viewName;
        }
        if (this.isBioGrader()) {
            viewName += '_Report';
        }

        return `${this.state.service?.code.toLowerCase()}_${viewName}`;
    }
}
